/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 * Optionally checks for creator role
 */

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { getUserRole, hasCreatorAccess } from '@/shared/lib';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireCreator?: boolean;
  requireParticipant?: boolean;
  showForbidden?: boolean;
}

export function ProtectedRoute({
  children,
  requireCreator = false,
  requireParticipant = false,
  showForbidden = false
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [isCheckingRole, setIsCheckingRole] = useState(requireCreator || requireParticipant);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkRole = async () => {
      // If no role restriction, grant access
      if (!requireCreator && !requireParticipant) {
        setHasAccess(true);
        setIsCheckingRole(false);
        return;
      }

      if (isAuthenticated && user) {
        console.log('[ProtectedRoute] Checking role for user:', user.uid);
        const role = await getUserRole(user.uid);

        let access = false;
        if (requireCreator) {
          access = hasCreatorAccess(role);
          console.log('[ProtectedRoute] User has creator access:', access);
        } else if (requireParticipant) {
          // Participant access: role must be 'participant' (not creator or admin)
          access = !hasCreatorAccess(role);
          console.log('[ProtectedRoute] User has participant access:', access, '(role:', role, ')');
        }

        setHasAccess(access);
        setIsCheckingRole(false);
      }
    };

    if (!authLoading) {
      checkRole();
    }
  }, [isAuthenticated, authLoading, user, requireCreator, requireParticipant]);

  // Show loading while checking auth or role
  if (authLoading || isCheckingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--color-accent-primary] mx-auto mb-4"></div>
          <p className="text-[--color-text-secondary]">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If role requirement is not met
  if ((requireCreator || requireParticipant) && !hasAccess) {
    if (showForbidden) {
      const redirectPath = requireCreator ? '/participant' : '/creator';
      const restrictedTo = requireCreator ? 'content creators' : 'participants';

      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md p-8">
            <h1 className="text-4xl font-bold text-[--color-text-primary] mb-4">403</h1>
            <h2 className="text-2xl font-semibold text-[--color-text-primary] mb-4">Access Forbidden</h2>
            <p className="text-[--color-text-secondary] mb-6">
              You don't have permission to access this page. This area is restricted to {restrictedTo}.
            </p>
            <a
              href={redirectPath}
              className="inline-block px-6 py-3 bg-[--color-accent-primary] text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Go to Your Dashboard
            </a>
          </div>
        </div>
      );
    }
    const redirectPath = requireCreator ? '/participant' : '/creator';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
